/**
 * LinkTimeJoinPage — Public guest join page for LinkTime
 * 
 * URL: /link/:roomAlias/:meetingCode
 * No authentication required
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';
import PhoneCall from './PhoneCall';
import PhonePreJoin from './PhonePreJoin';
import LinkTimeChat from './LinkTimeChat';
import { authFetch, getAuthHeader } from '../../auth';
import './LinkTimeJoinPage.css';

const LinkTimeJoinPage = () => {
  const { alias: roomAlias, inviteCode: meetingCode } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);

  const [displayName, setDisplayName] = useState(() => {
    try { return localStorage.getItem('lt_guest_name_' + roomAlias + '_' + meetingCode) || ''; } catch(e) { return ''; }
  });
  const [password, setPassword] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const [joinToken, setJoinToken] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [callData, setCallData] = useState(null);
  const [isHost, setIsHost] = useState(false);

  const [notifyStatus, setNotifyStatus] = useState(null);
  const [notifyGuestsStatus, setNotifyGuestsStatus] = useState(null);

  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [guestEmail, setGuestEmail] = useState(() => {
    try { return localStorage.getItem('lt_guest_email_' + roomAlias + '_' + meetingCode) || ''; } catch(e) { return ''; }
  });
  const [guestPhone, setGuestPhone] = useState(() => {
    try { return localStorage.getItem('lt_guest_phone_' + roomAlias + '_' + meetingCode) || ''; } catch(e) { return ''; }
  });
  const [wantsHostAlert, setWantsHostAlert] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(() => {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return 'unsupported';
    return window.Notification.permission;
  });
  const [notifyRequested, setNotifyRequested] = useState(false);

  const selfTz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
  const previousHostOnlineRef = useRef(false);

  useEffect(function() {
    try { localStorage.setItem('lt_guest_name_' + roomAlias + '_' + meetingCode, displayName); } catch(e) {}
    return function() {
      try { localStorage.removeItem('lt_guest_name_' + roomAlias + '_' + meetingCode); } catch(e) {}
    };
  }, [displayName, roomAlias, meetingCode]);

  useEffect(function() {
    try { localStorage.setItem('lt_guest_email_' + roomAlias + '_' + meetingCode, guestEmail); } catch(e) {}
    return function() {
      try { localStorage.removeItem('lt_guest_email_' + roomAlias + '_' + meetingCode); } catch(e) {}
    };
  }, [guestEmail, roomAlias, meetingCode]);

  useEffect(function() {
    try { localStorage.setItem('lt_guest_phone_' + roomAlias + '_' + meetingCode, guestPhone); } catch(e) {}
    return function() {
      try { localStorage.removeItem('lt_guest_phone_' + roomAlias + '_' + meetingCode); } catch(e) {}
    };
  }, [guestPhone, roomAlias, meetingCode]);

  const effectiveDisplayName = () => {
    const explicit = displayName.trim();
    if (explicit) return explicit;
    return roomInfo?.meeting_name || roomInfo?.room_name || 'Guest';
  };

  const loadMeetingInfo = async (isPoll = false) => {
    try {
      // 1. Check URL params for host token IMMEDIATELY
      const urlParams = new URLSearchParams(window.location.search);
      const hostToken = urlParams.get('hostToken');
      const hostName = urlParams.get('hostName');
      const wsUrl = urlParams.get('wsUrl');
      const room = urlParams.get('room');
      const isHostFlag = urlParams.get('host') === '1';

      // force_guest parameter skips all host detection
      var forceGuest = urlParams.get('force_guest');
      if (forceGuest === '1') {
        setIsHost(false);
        try { sessionStorage.setItem('lt_guest_' + roomAlias + '_' + meetingCode, '1'); } catch(e) {}
      }

      // Host arrived via authenticated redirect — flag as host immediately (skip if force_guest)
      if (isHostFlag && !isHost && forceGuest !== '1') {
        setIsHost(true);
        try { localStorage.setItem('lt_host_' + roomAlias + '_' + meetingCode, '1'); } catch(e) {}
      }

      // Survive page reload: check localStorage for host flag (skip if force_guest)
      if (!isHost && !isHostFlag && forceGuest !== '1') {
        try {
          if (localStorage.getItem('lt_host_' + roomAlias + '_' + meetingCode) === '1') {
            setIsHost(true);
          }
        } catch(e) {}
      }

      // Auth-based host detection: if logged in and not in guest mode, check if user owns this room
      if (!isHost && !isHostFlag && forceGuest !== '1') {
        try {
          var guestOverride = sessionStorage.getItem('lt_guest_' + roomAlias + '_' + meetingCode);
          if (guestOverride === '1') {
            // User explicitly chose guest mode — skip auto host detection
          } else {
            var authRes = await authFetch('/api/linktime/join/' + roomAlias + '/' + meetingCode + '/am-i-host');
            if (authRes.ok) {
              var authData = await authRes.json();
              if (authData.is_host) {
                setIsHost(true);
                localStorage.setItem('lt_host_' + roomAlias + '_' + meetingCode, '1');
              }
            }
          }
        } catch(e) {}
      }

      // Host arrived via hostToken in URL
      if (hostToken && hostName && wsUrl && !inCall && !joining) {
        console.log('LinkTime: Host auto-join triggered', {
          hostName: decodeURIComponent(hostName),
          wsUrl: decodeURIComponent(wsUrl),
          room: room ? decodeURIComponent(room) : 'Room'
        });
        setDisplayName(decodeURIComponent(hostName));
        setIsHost(true);
        try { localStorage.setItem('lt_host_' + roomAlias + '_' + meetingCode, '1'); } catch(e) {}
        setCallData({
          token: decodeURIComponent(hostToken),
          wsUrl: decodeURIComponent(wsUrl),
          room: room ? decodeURIComponent(room) : 'Room'
        });
        // Host notification links should land on the prejoin screen. Starting
        // LiveKit immediately triggers browser permission prompts and publishes
        // camera/mic before the host has made an explicit choice.

        // Clear URL parameters immediately after parsing
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }

      // 2. Load meeting info for the UI (stats, name, etc.)
      const res = await fetch(`/api/linktime/join/${roomAlias}/${meetingCode}`);
      if (res.ok) {
        const data = await res.json();
        setRoomInfo(data);
        setError(null); // Clear error if poll succeeds
      } else {
        // If it's a 429 (Too many requests), ignore for polls
        if (res.status === 429 && isPoll) return;

        // Only throw error if we are NOT a host. Host should see the room anyway.
        if (!hostToken && !isHost) {
          const data = await res.json();
          throw new Error(data.error || 'Meeting not found');
        }
      }
    } catch (err) {
      // Don't show full-page error on background polls if we already have room info
      if (!roomInfo && !isHost) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Polling for room status and waiting guests
  useEffect(() => {
    loadMeetingInfo(false);
    
    // Poll fast while waiting for host (3s); slow once we're in the call (30s)
    const pollMs = inCall ? 30000 : 3000;
    const interval = setInterval(() => loadMeetingInfo(true), pollMs);
    return () => clearInterval(interval);
  }, [roomAlias, meetingCode, inCall]);

  // Host presence heartbeat
  useEffect(() => {
    if (!isHost) return;
    // Send heartbeat even before joining call — signals host is on the join page
    if (!roomAlias || !meetingCode) return;

    const sendHeartbeat = async () => {
      try {
        await fetch(`/api/linktime/join/${roomAlias}/${meetingCode}/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_host: true })
        });
      } catch (e) {
        console.warn('Host heartbeat failed', e);
      }
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000); // 30s for pre-join, 60s when in call
    return () => clearInterval(interval);
  }, [isHost, roomAlias, meetingCode]);

  // Guest presence heartbeat — registers with server, adds to waiting_guests on first beat
  useEffect(() => {
    if (isHost) return;
    if (!roomAlias || !meetingCode) return;

    const sendGuestHeartbeat = async () => {
      try {
        await fetch(`/api/linktime/join/${roomAlias}/${meetingCode}/guest-heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            guest_name: effectiveDisplayName(),
            role: 'guest',
            guest_email: guestEmail.trim() || null,
            guest_phone: guestPhone.trim() || null,
            tz: selfTz
          })
        });
      } catch (e) {
        console.warn('Guest heartbeat failed', e);
      }
    };

    sendGuestHeartbeat();
    const interval = setInterval(sendGuestHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [isHost, roomAlias, meetingCode, displayName, guestEmail, guestPhone, inCall]);

  const requestBrowserNotificationPermission = async () => {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
      setBrowserPermission('unsupported');
      return false;
    }
    if (window.Notification.permission === 'granted') {
      setBrowserPermission('granted');
      return true;
    }
    if (window.Notification.permission === 'denied') {
      setBrowserPermission('denied');
      return false;
    }
    const permission = await window.Notification.requestPermission();
    setBrowserPermission(permission);
    return permission === 'granted';
  };

  const handleHostAlertToggle = async (enabled) => {
    if (!enabled) {
      setWantsHostAlert(false);
      return;
    }
    const granted = await requestBrowserNotificationPermission();
    if (!granted) {
      setJoinError('Allow browser notifications if you want an alert when the host joins.');
      return;
    }
    setJoinError(null);
    setWantsHostAlert(true);
  };

  // Browser notification when host arrives
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.Notification === 'undefined') return;
    const hostOnline = Boolean(roomInfo?.host_online);
    const hostJustArrived = hostOnline && !previousHostOnlineRef.current;
    if (!isHost && hostJustArrived && notifyRequested && wantsHostAlert && browserPermission === 'granted') {
      const title = `${roomInfo?.owner_name || 'Your host'} is ready`;
      const body = `${roomInfo?.meeting_name || roomInfo?.room_name || 'The room'} is open now.`;
      new window.Notification(title, { body });
    }
    previousHostOnlineRef.current = hostOnline;
  }, [browserPermission, isHost, notifyRequested, wantsHostAlert, roomInfo?.host_online, roomInfo?.meeting_name, roomInfo?.owner_name]);

  // Notify host (can be called before or after joining)
  const handleNotifyHost = async (silent) => {
    if (!silent && wantsHostAlert && browserPermission !== 'granted') {
      setNotifyStatus('error');
      setJoinError('Allow browser notifications if you want an alert when the host joins.');
      setTimeout(() => setNotifyStatus(null), 3000);
      return;
    }
    if (!silent) setNotifyStatus('sending');
    setJoinError(null);
    try {
      const res = await fetch(`/api/linktime/room/${roomAlias}/${meetingCode}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: '\u{1F44B} Guest is waiting',
          display_name: effectiveDisplayName()
        })
      });
      if (!res.ok) throw new Error('Failed to send ping');
      if (!silent) {
        setNotifyRequested(true);
        setNotifyStatus('sent');
        setTimeout(() => setNotifyStatus(null), 5000);
      }
    } catch (err) {
      if (!silent) {
        setNotifyStatus('error');
        setTimeout(() => setNotifyStatus(null), 3000);
      }
    }
  };

  const handleNotifyGuests = async () => {
    setNotifyGuestsStatus('sending');
    try {
      const res = await fetch(`/api/linktime/room/${roomAlias}/${meetingCode}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: '\u{1F4E2} Host is here!',
          display_name: effectiveDisplayName()
        })
      });
      if (!res.ok) throw new Error('Failed to send');
      setNotifyGuestsStatus('sent');
      setTimeout(() => setNotifyGuestsStatus(null), 10000);
    } catch (err) {
      setNotifyGuestsStatus('error');
      setTimeout(() => setNotifyGuestsStatus(null), 5000);
    }
  };

  // Join meeting (verify password if needed)
  const doJoin = async () => {
    // Host without callData (no email pre-fill) — fetch host token via authenticated endpoint
    if (isHost && !callData) {
      // If host detected but not logged in, redirect to login
      if (!localStorage.getItem('nf_token')) {
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
        return;
      }
      const meetingId = roomInfo?.meeting_id;
      if (!meetingId) {
        setJoinError('Meeting info not loaded yet — wait a moment and try again.');
        return;
      }
      setJoining(true);
      setJoinError(null);
      try {
        const res = await authFetch('/api/linktime/meetings/' + meetingId + '/host-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: effectiveDisplayName() })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to get host token');
        }
        const data = await res.json();
        setCallData({ token: data.token, wsUrl: data.wsUrl, room: data.room });
        setInCall(true);
      } catch (e) {
        setJoinError(e.message);
      } finally {
        setJoining(false);
      }
      return;
    }
    if (isHost && callData) {
      setInCall(true);
      return;
    }

    setJoining(true);
    setJoinError(null);
    
    try {
      if (!isHost) {
        handleNotifyHost(true).catch(() => {});
      }
      
      const res = await fetch(`/api/linktime/join/${roomAlias}/${meetingCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: effectiveDisplayName(),
          password: password
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to join');
      }
      
      const data = await res.json();
      setJoinToken(data.token);
      
      await getVideoToken(data.token);
    } catch (err) {
      setJoinError(err.message);
      setJoining(false);
    }
  };

  // Get video room token
  const getVideoToken = async (token) => {
    try {
      const res = await fetch(`/api/linktime/join/${roomAlias}/${meetingCode}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          display_name: effectiveDisplayName()
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to connect');
      }
      
      const data = await res.json();
      setCallData(data);
      setInCall(true);
    } catch (err) {
      setJoinError(err.message);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="linktime-join-page">
        <div className="linktime-join-loading">
          <div className="linktime-join-spinner" />
          <p>Loading room...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    const isRateLimited = error.toLowerCase().includes('too many requests');
    
    return (
      <div className="linktime-join-page">
        <div className="linktime-join-error-container">
          <div className="linktime-join-logo">
            <span className="logo-hash">#</span> NumFree
          </div>
          <div className="linktime-join-error-card">
            <h2>{isRateLimited ? 'Please Wait' : 'Unable to Join'}</h2>
            <p className="linktime-join-error-message">{error}</p>
            <p className="linktime-join-error-hint">
              {isRateLimited 
                ? 'We are receiving too many requests. Please wait a few seconds and try again.'
                : 'This link may have expired, been revoked, or the meeting details are incorrect.'}
            </p>
            <div className="linktime-join-error-actions">
              <button 
                className="linktime-join-btn" 
                onClick={() => { setError(null); setLoading(true); loadMeetingInfo(false); }}
              >
                Try Again
              </button>
              <Link to="/" className="linktime-join-home-link">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // In call or pre-join — chat is always mounted (stable key, no phantom leave)
  var inCallActive = inCall && callData;
  return (
    <div className={'linktime-join-page' + (inCallActive ? ' linktime-join-page--call' : '')}>
      {inCallActive ? (
        <LiveKitRoom
          serverUrl={callData.wsUrl}
          token={callData.token}
          connect={true}
          video={Boolean(videoEnabled)}
          audio={Boolean(audioEnabled)}
          onDisconnected={() => { setInCall(false); setCallData(null); }}
          >
            <PhoneCall 
              isHost={isHost} 
              roomInfo={roomInfo}
              roomAlias={roomAlias}
              meetingCode={meetingCode} 
              initialVideo={videoEnabled}
              initialAudio={audioEnabled}
              onLeave={() => { setInCall(false); setCallData(null); }}
              hideChat={true}
            />
            <RoomAudioRenderer />
          </LiveKitRoom>
      ) : (
        <PhonePreJoin
          isHost={isHost}
          roomInfo={roomInfo}
          roomAlias={roomAlias}
          meetingCode={meetingCode}
          joining={joining}
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          setAudioEnabled={setAudioEnabled}
          setVideoEnabled={setVideoEnabled}
          joinError={joinError}
          onJoin={doJoin}
          displayName={displayName}
          setDisplayName={setDisplayName}
          password={password}
          setPassword={setPassword}
          guestEmail={guestEmail}
          setGuestEmail={setGuestEmail}
          guestPhone={guestPhone}
          setGuestPhone={setGuestPhone}
          notifyStatus={notifyStatus}
          handleNotifyHost={handleNotifyHost}
          wantsHostAlert={wantsHostAlert}
          handleHostAlertToggle={handleHostAlertToggle}
          browserPermission={browserPermission}
          notifyGuestsStatus={notifyGuestsStatus}
          handleNotifyGuests={handleNotifyGuests}
          onLeaveHostMode={() => {
            try { sessionStorage.setItem('lt_guest_' + roomAlias + '_' + meetingCode, '1'); } catch(e) {}
            try { localStorage.removeItem('lt_host_' + roomAlias + '_' + meetingCode); } catch(e) {}
            window.location.reload();
          }}
        />
      )}
      <LinkTimeChat
        roomAlias={roomAlias}
        meetingCode={meetingCode}
        selfName={effectiveDisplayName()}
        selfRole={isHost ? 'host' : 'guest'}
        selfTz={selfTz}
        layout={inCallActive ? 'rail' : 'drawer'}
      />
    </div>
  );
};

export default LinkTimeJoinPage;
